import React from 'react';
import { Shield, Lock, Eye, User, Database, Globe, Mail, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: 'introduction',
      title: '1. Introduction',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Welcome to <strong>TypeSync</strong> — a productivity tool that allows you to expand custom shortcut tags into full snippets using a mobile dashboard and Chrome extension.
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            Your privacy is important to us. This Privacy Policy explains what information we collect, how we use it, and your rights regarding that information.
          </p>
        </div>
      )
    },
    {
      id: 'information-we-collect',
      title: '2. Information We Collect',
      icon: Database,
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              a. Personal Information
            </h4>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
              <li>Your <strong>Google account email and display name</strong> via Firebase Authentication</li>
              <li>Authentication tokens for managing secure sessions</li>
            </ul>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              b. Snippet Data
            </h4>
            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
              <li>Snippets and tags that you create and save in your personal account (e.g., <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm">/py1</code>, <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm">/note</code>, etc.)</li>
              <li>Typing speed preferences set by you</li>
            </ul>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 p-4 rounded-r-lg">
            <p className="text-blue-700 dark:text-blue-300">
              💡 <strong>Note:</strong> We <strong>do not collect</strong> passwords, payment information, or sensitive personal content.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'how-we-use-information',
      title: '3. How We Use Your Information',
      icon: Eye,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            We use your information for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
            <li>To authenticate your login via Firebase</li>
            <li>To store and sync your personal shortcut tags/snippets across devices</li>
            <li>To improve your experience and maintain security</li>
          </ul>
          <div className="bg-green-50 dark:bg-green-900/30 border-l-4 border-green-400 p-4 rounded-r-lg">
            <p className="text-green-700 dark:text-green-300 font-medium">
              We do <strong>not sell</strong>, share, or disclose your personal information to third-party advertisers.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'data-storage-security',
      title: '4. Data Storage and Security',
      icon: Lock,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Your data is securely stored using <strong>Google Firebase services</strong>, which include:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
            <li>Firebase Authentication</li>
            <li>Firebase Realtime Database (for storing snippets)</li>
            <li>SecureToken service for session management</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300">
            We follow industry-standard best practices to ensure your data is encrypted and secure.
          </p>
        </div>
      )
    },
    {
      id: 'permissions',
      title: '5. Permissions Used',
      icon: User,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            The TypeSync Chrome Extension requires the following permissions:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
            <li><code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm">identity</code> – to log in with Google</li>
            <li><code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm">storage</code> – to save local preferences</li>
            <li><code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm">host permissions</code> – to securely communicate with Firebase-hosted services</li>
          </ul>
          <p className="text-gray-700 dark:text-gray-300 font-medium">
            We do not access or monitor your browsing history, passwords, or personal files.
          </p>
        </div>
      )
    },
    {
      id: 'your-rights',
      title: '6. Your Rights',
      icon: Shield,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            You have the right to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
            <li>View and manage the data you upload (via your dashboard)</li>
            <li>Delete your account and all associated data</li>
            <li>Revoke access to your Google account at any time</li>
          </ul>
        </div>
      )
    },
    {
      id: 'childrens-privacy',
      title: '7. Children\'s Privacy',
      icon: User,
      content: (
        <p className="text-gray-700 dark:text-gray-300">
          TypeSync is not intended for use by individuals under the age of 13. We do not knowingly collect data from children.
        </p>
      )
    },
    {
      id: 'changes',
      title: '8. Changes to This Policy',
      icon: Globe,
      content: (
        <p className="text-gray-700 dark:text-gray-300">
          We may update this Privacy Policy from time to time. Changes will be posted on this page with a new effective date.
        </p>
      )
    },
    {
      id: 'contact',
      title: '9. Contact Us',
      icon: Mail,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            If you have questions or concerns about this Privacy Policy, please contact:
          </p>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300">
              🌐 Website: <a href="https://typesync-18a8f.web.app" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">https://typesync-18a8f.web.app</a>
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Privacy Policy
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Effective Date: July 21, 2025
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {sections.map((section) => {
            const IconComponent = section.icon;
            return (
              <div
                key={section.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                      {section.title}
                    </h2>
                    <div className="prose dark:prose-invert max-w-none">
                      {section.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-6">
            <p className="text-blue-700 dark:text-blue-300 font-medium">
              Thank you for using TypeSync! 🚀
            </p>
            <p className="text-blue-600 dark:text-blue-400 mt-2">
              Your privacy and security are our top priorities.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
